/**
 * RichShotDetailSummary Component
 *
 * Table-based summary with interactive hierarchical breakdown rows.
 * Shot-only version — no action metrics (PPP, xPPP, TOV%, ORB%).
 *
 * Columns:
 * Shots | FG% | 2PT% | 3PT% | TS% | Ast% | Rim Rate (TS%) | Mid Rate (TS%) | 3PT Rate (TS%)
 *
 * Breakdown rows appear when expandedGroups is non-empty.
 * Hover/click on rows drives chart highlighting via callbacks.
 */

import { useMemo } from "react";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Shot, ShotFilters } from "./types";
import {
  applyFilters,
  calculateShotOnlyRowStats,
  buildShotHierarchicalRows,
  formatShotGroupLabel,
  ShotRowStats,
  ShotHierarchicalRow,
} from "./utils";
import "./ShotDetailModal.scss";

// ============================================================================
// Formatters
// ============================================================================

const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;
const fmtTs = (v: number | null) =>
  v !== null ? `${(v * 100).toFixed(1)}% TS` : "--";
const fmtRate = (v: number) => (v * 100).toFixed(1);

// ============================================================================
// Component
// ============================================================================

interface RichShotDetailSummaryProps {
  shots: Shot[];
  filters: ShotFilters;
  expandedGroups: string[];
  onRowHover?: (key: string | null, shots: Shot[] | null) => void;
  onRowClick?: (key: string, shots: Shot[]) => void;
  selectedRowKey: string | null;
}

export default function RichShotDetailSummary({
  shots,
  filters,
  expandedGroups,
  onRowHover,
  onRowClick,
  selectedRowKey,
}: RichShotDetailSummaryProps) {
  const { overallStats, hierarchicalRows } = useMemo(() => {
    const filtered = applyFilters(shots, filters);
    const overall = calculateShotOnlyRowStats(filtered);

    let rows: ShotHierarchicalRow[] = [];
    if (expandedGroups.length > 0) {
      rows = buildShotHierarchicalRows(filtered, expandedGroups);
    }

    return { overallStats: overall, hierarchicalRows: rows };
  }, [shots, filters, expandedGroups]);

  const totalShots = overallStats.shotCount;
  const hasBreakdown = expandedGroups.length > 0;
  const headerLabel = hasBreakdown
    ? expandedGroups
        .map(formatShotGroupLabel)
        .reduce<React.ReactNode[]>((acc, label, i) => {
          if (i > 0)
            acc.push(
              <KeyboardDoubleArrowRight
                key={`sep-${i}`}
                sx={{
                  fontSize: 14,
                  verticalAlign: "middle",
                  mx: 0.25,
                  opacity: 0.7,
                }}
              />,
            );
          acc.push(<span key={i}>{label}</span>);
          return acc;
        }, [])
    : "Summary";

  return (
    <div
      className={`summary-table-scroll${hasBreakdown ? " summary-table-scroll--breakdown" : ""}`}
    >
      <table className="summary-table">
        <thead>
          <tr>
            <th className="summary-table__label">{headerLabel}</th>
            <th>Shots</th>
            <th>FG%</th>
            <th>2PT%</th>
            <th>3PT%</th>
            <th>TS%</th>
            <th>Ast%</th>
            <th>Rim Rate</th>
            <th>Mid Rate</th>
            <th>3PT Rate</th>
          </tr>
        </thead>
        <tbody>
          {/* Overall row */}
          <ShotSummaryRow
            label="Overall"
            stats={overallStats}
            isOverall
            parentShotCount={totalShots}
            depth={0}
            hasBreakdown={hasBreakdown}
          />
          {/* Hierarchical breakdown rows */}
          {hierarchicalRows.map((row) => (
            <ShotSummaryRow
              key={row.rowKey}
              label={row.label}
              stats={row.stats}
              parentShotCount={row.parentShotCount}
              depth={row.depth}
              isGroupHeader={row.isGroupHeader}
              rowKey={row.rowKey}
              shots={row.shots}
              isSelected={selectedRowKey === row.rowKey}
              onHover={onRowHover}
              onClick={onRowClick}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Table Row
// ============================================================================

interface ShotSummaryRowProps {
  label: string;
  stats: ShotRowStats;
  isOverall?: boolean;
  isGroupHeader?: boolean;
  hasBreakdown?: boolean;
  parentShotCount: number;
  depth: number;
  rowKey?: string;
  shots?: Shot[];
  isSelected?: boolean;
  onHover?: (key: string | null, shots: Shot[] | null) => void;
  onClick?: (key: string, shots: Shot[]) => void;
}

function ShotSummaryRow({
  label,
  stats,
  isOverall,
  isGroupHeader,
  hasBreakdown,
  parentShotCount,
  depth,
  rowKey,
  shots,
  isSelected,
  onHover,
  onClick,
}: ShotSummaryRowProps) {
  const pctOfParent =
    parentShotCount > 0 ? (stats.shotCount / parentShotCount) * 100 : 0;

  // Build CSS class list
  let rowClass = "summary-table__row";
  if (isOverall)
    rowClass = hasBreakdown
      ? "summary-table__overall"
      : "summary-table__overall summary-table__overall--no-breakdown";
  if (isGroupHeader) rowClass += " summary-table__group-header";
  if (depth >= 2) rowClass += ` summary-table__depth-${Math.min(depth, 3)}`;
  if (isSelected) rowClass += " summary-table__row--selected";

  const labelPadding = isOverall ? 8 : 8 + depth * 16;
  const isInteractive = !isOverall && rowKey && shots;

  return (
    <tr
      className={rowClass}
      onMouseEnter={
        isInteractive ? () => onHover?.(rowKey!, shots!) : undefined
      }
      onMouseLeave={isInteractive ? () => onHover?.(null, null) : undefined}
      onClick={isInteractive ? () => onClick?.(rowKey!, shots!) : undefined}
      style={{ cursor: isInteractive ? "pointer" : undefined }}
    >
      <td
        className="summary-table__label"
        style={{ paddingLeft: labelPadding }}
      >
        {label}
      </td>

      {/* Shots */}
      <td>
        {stats.shotCount.toLocaleString()}
        {!isOverall && (
          <span className="summary-table__sub">
            ({pctOfParent.toFixed(1)}%)
          </span>
        )}
      </td>

      {/* FG% | 2PT% | 3PT% | TS% | Ast% */}
      <td>{stats.fgPct !== null ? fmtPct(stats.fgPct) : "--"}</td>
      <td>{stats.twoPtPct !== null ? fmtPct(stats.twoPtPct) : "--"}</td>
      <td>{stats.threePtPct !== null ? fmtPct(stats.threePtPct) : "--"}</td>
      <td>{stats.tsPct !== null ? fmtPct(stats.tsPct) : "--"}</td>
      <td>{stats.assistedPct !== null ? fmtPct(stats.assistedPct) : "--"}</td>

      {/* Zone rates with TS% sub-values */}
      <td>
        {fmtRate(stats.rimRate)}
        <span className="summary-table__sub">{fmtTs(stats.rimTs)}</span>
      </td>
      <td>
        {fmtRate(stats.midRate)}
        <span className="summary-table__sub">{fmtTs(stats.midTs)}</span>
      </td>
      <td>
        {fmtRate(stats.threeRate)}
        <span className="summary-table__sub">{fmtTs(stats.threeTs)}</span>
      </td>
    </tr>
  );
}
